import { RotateCcw, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { CustomerDetail } from '@/lib/api/types'
import { formatDate } from '@/lib/format'
import { useCloseCustomer, useReopenCustomer } from './api'

/** Shown on a closed workspace: when it's deleted for good, and Reopen while there's time. */
export function ClosedBanner({ customer, canManage }: { customer: CustomerDetail; canManage: boolean }) {
  const reopen = useReopenCustomer(customer.id)
  if (!customer.closed_at || !customer.deletes_on) return null

  return (
    <div
      role="status"
      className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-danger-soft px-4 py-3 text-[13px] text-ink"
    >
      <p className="flex items-start gap-2">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden="true" />
        <span>
          Closed on {formatDate(customer.closed_at)}
          {customer.owner?.account_closed ? ', with the owner’s account' : ''}. Everything in it is deleted
          for good on <span className="font-semibold">{formatDate(customer.deletes_on)}</span>. Until then you
          can send the customer an export, or reopen it.
        </span>
      </p>
      {canManage && (
        <Button
          variant="secondary"
          loading={reopen.isPending}
          onClick={() =>
            reopen.mutate(undefined, {
              onSuccess: () =>
                toast.success('Workspace reopened. Its websites, rank checks and report emails are back on.'),
              onError: (err) => toast.error(errorMessage(err)),
            })
          }
        >
          <RotateCcw /> Reopen
        </Button>
      )}
    </div>
  )
}

/** Owners and admins: close a workspace when its owner asks (Terms: "contact us"). */
/**
 * A Free workspace nobody uses closes on day 90 (the API's InactiveWorkspaces): once its first
 * reminder has gone, support sees when, so they can explain it or tell the customer how to keep it.
 */
export function UnusedBanner({ customer }: { customer: CustomerDetail }) {
  const unused = customer.inactivity
  if (!unused || unused.reminders_sent === 0) return null
  return (
    <p className="mb-6 rounded-md border border-warning/40 bg-warning-soft px-4 py-3 text-[13px] text-warning">
      Nobody has used this Free workspace since {formatDate(unused.last_used_at)}. It closes on{' '}
      {formatDate(unused.closes_on)} unless someone in it signs in ({unused.reminders_sent} of 3 reminders
      sent).
    </p>
  )
}

export function CloseWorkspaceCard({ customer }: { customer: CustomerDetail }) {
  const [open, setOpen] = useState(false)
  if (customer.closed_at) return null

  return (
    <Card>
      <CardHeader
        title="Close this workspace"
        description="Only when the owner asks. Members lose access straight away, nothing runs for it any more, and everything is deleted for good after 30 days."
      />
      <CardBody>
        <Button variant="danger" onClick={() => setOpen(true)}>
          Close workspace
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          {open && (
            <DialogContent className="max-w-lg" title={`Close ${customer.name}?`}>
              <CloseForm customer={customer} onDone={() => setOpen(false)} />
            </DialogContent>
          )}
        </Dialog>
      </CardBody>
    </Card>
  )
}

function CloseForm({ customer, onDone }: { customer: CustomerDetail; onDone: () => void }) {
  const close = useCloseCustomer(customer.id)
  const [form, setForm] = useState({
    confirm_name: '',
    close_owner_account: false,
    note: '',
    confirm_lifetime: false,
  })
  // Closing ends a lifetime plan without a refund; it can be restored if the customer asks within 30 days.
  const lifetime = customer.lifetime?.plan ?? null
  const errors = isApiError(close.error) ? close.error.errors : {}
  const general = isApiError(close.error) && !close.error.isValidation ? close.error.message : null

  return (
    <form
      noValidate
      className="grid gap-4 text-[13px]"
      onSubmit={(e) => {
        e.preventDefault()
        const { confirm_lifetime: confirmLifetime, ...rest } = form
        close.mutate(lifetime ? { ...rest, confirm_lifetime: confirmLifetime } : rest, {
          onSuccess: () => {
            toast.success('Workspace closed. The owner has been emailed.')
            onDone()
          },
          onError: (err) => !isApiError(err) && toast.error(errorMessage(err)),
        })
      }}
    >
      <ul className="grid list-disc gap-1 pl-5 text-ink-muted">
        <li>Members can&apos;t open it any more, and its websites are archived.</li>
        <li>Rank checks, syncs and report emails stop.</li>
        <li>The owner gets an email with the date everything is deleted for good: 30 days from now.</li>
        <li>Billing records stay for the books, without email or card details.</li>
      </ul>
      {general && <p className="rounded-md bg-danger-soft px-3 py-2 text-ink">{general}</p>}
      {lifetime && (
        <label className="flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2 text-ink">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-[var(--color-accent)]"
            checked={form.confirm_lifetime}
            onChange={(e) => setForm({ ...form, confirm_lifetime: e.target.checked })}
          />
          <span>
            This workspace has {lifetime.name}. Closing it ends the lifetime plan without a refund. Confirm
            that the customer asked for this; it can be restored if they tell us within 30 days.
          </span>
        </label>
      )}
      <label className="flex items-start gap-2 text-ink">
        <input
          type="checkbox"
          className="mt-0.5 size-4 accent-[var(--color-accent)]"
          checked={form.close_owner_account}
          onChange={(e) => setForm({ ...form, close_owner_account: e.target.checked })}
        />
        <span>
          Also close the owner&apos;s account{customer.owner ? ` (${customer.owner.email})` : ''}. They
          can&apos;t sign in any more, and it&apos;s deleted with the workspace. Only possible if they belong
          to no other workspace.
        </span>
      </label>
      <Field label="Note for the audit log (optional)" htmlFor="close-note" error={errors.note?.[0]}>
        <Textarea
          id="close-note"
          rows={2}
          placeholder="e.g. Asked by email on October 10"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />
      </Field>
      <Field
        label={`Type ${customer.name} to confirm`}
        htmlFor="close-confirm"
        required
        error={errors.confirm_name?.[0]}
      >
        <Input
          id="close-confirm"
          autoComplete="off"
          value={form.confirm_name}
          onChange={(e) => setForm({ ...form, confirm_name: e.target.value })}
        />
      </Field>
      <div className="flex justify-end gap-2">
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          type="submit"
          variant="danger"
          loading={close.isPending}
          disabled={
            form.confirm_name.trim() !== customer.name || (lifetime !== null && !form.confirm_lifetime)
          }
        >
          Close workspace
        </Button>
      </div>
    </form>
  )
}
